import { Grid, Stack, Typography } from '@mui/material';
import { MprcChip, MprcIconButtonDropdown } from 'components';
import { MprcSearchTextField, MprcSearchTextFieldConfirmMode, MprcSearchTextFieldHandle } from 'components/MprcSearchTextField';
import { MprcFilterLine } from 'components/filter';
import { IconSort } from 'components/icons';
import { Property } from "csstype";
import { CoreFilterCommand } from 'generated/CoreApi_v1';
import useDidUpdateEffect from 'hooks/useNoInitialEffect';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdvancedFilterDropdown, AdvFilterStandardPicker, IAdvFilterCommand, IAdvFilterDropdown, IAdvFilterMenuItem, IAdvFilterParams, IAdvFilterResult } from 'views/general/AdvancedFilter';
import { ChannelPicker } from 'views/general/AdvancedFilter/Pickers/ChannelPicker';
import { CustomFieldPicker } from 'views/general/AdvancedFilter/Pickers/CustomFieldPicker';
import { DateFieldPicker } from 'views/general/AdvancedFilter/Pickers/DateFieldPicker';
import { GroupPicker } from 'views/general/AdvancedFilter/Pickers/GroupPicker';
import { LanguagePicker } from 'views/general/AdvancedFilter/Pickers/LanguagePicker';
import { StickerPicker } from 'views/general/AdvancedFilter/Pickers/StickerPicker';
import { SwitchPicker } from 'views/general/AdvancedFilter/Pickers/SwitchPicker';
import { TagPicker } from 'views/general/AdvancedFilter/Pickers/TagPicker';
import { TextFieldPicker } from 'views/general/AdvancedFilter/Pickers/TextFieldPicker';
import { TopicHierarchicalPicker } from 'views/general/AdvancedFilter/Pickers/TopicHierarchicalPicker';
import { TopicPicker } from 'views/general/AdvancedFilter/Pickers/TopicPicker';
import { TrainingPicker } from 'views/general/AdvancedFilter/Pickers/TrainingPicker';
import { UnitPicker } from 'views/general/AdvancedFilter/Pickers/UnitPicker';
import { UserPicker } from 'views/general/AdvancedFilter/Pickers/UserPicker';
import { WorkplacePicker } from 'views/general/AdvancedFilter/Pickers/WorkplacePicker';
import { IAdvFilterPickerProps } from 'views/general/AdvancedFilter/interfaces';
import { PeriodPicker } from './Pickers/PeriodPicker';
////////////////////////////////////////////////////////////////////////////

export interface IAdvFilterGroupResult {
    command: any;
    title: string;
    results: IAdvFilterResult[];
}

export interface IQuickFilterProps {
    command: CoreFilterCommand;
    onRemoveQuickFilter: (filter: IAdvFilterResult) => void;
}

interface IProps<TOrderBy> {
    title?: string;
    filter: IAdvFilterParams<TOrderBy>;
    searchTextConfirmMode?: MprcSearchTextFieldConfirmMode;
    searchTextInFilterLine?: boolean;

    leftDropdownAlign?: Property.JustifyContent;
    leftDropdowns: IAdvFilterDropdown[];
    beforeLeftDropdown?: React.ReactNode[];

    rightDropdowns?: IAdvFilterDropdown[];
    afterRigthDropdown?: React.ReactNode[];

    orderMenuOptions?: IAdvFilterMenuItem<TOrderBy>[];
    quickFilter?: IQuickFilterProps;

    searchComponent?: React.ReactNode;
    startComponent?: React.ReactNode | React.ReactNode[];
    endComponent?: React.ReactNode | React.ReactNode[];
    centerComponent?: React.ReactNode;
    chipsPosition?: "bottom" | "top";
    message?: React.ReactNode;
    placeholder?: string;
    onEditing?: (value: boolean) => void;
    onFilterChange: (filter: IAdvFilterParams<TOrderBy>, enterPressed: boolean) => void;
    onGetStickers?: () => Promise<boolean>;
    onError: (e: any) => void;
}

/**
 * Filtro Avançados para filtros
 * @param props
 */
export const AdvancedFilter = <TOrderBy extends unknown>(props: IProps<TOrderBy>) => {
    const {
        title, filter, quickFilter,
        beforeLeftDropdown, leftDropdownAlign = "left", leftDropdowns,
        rightDropdowns, afterRigthDropdown, orderMenuOptions,
        searchComponent, searchTextConfirmMode = "debounce", searchTextInFilterLine = false, placeholder,
        startComponent, endComponent, centerComponent,
        chipsPosition = "bottom", message,
        onFilterChange, onGetStickers, onError, onEditing
    } = props;

    const { t } = useTranslation("shared");
    const [filterPicker, setFilterPicker] = useState<IAdvFilterCommand | null>(null);
    const [order, setOrder] = useState<any>(filter.order);
    const orderOptions = useRef<IAdvFilterMenuItem<TOrderBy>[] | null>(orderMenuOptions ?? null);
    const searchRef = useRef<MprcSearchTextFieldHandle | null>(null);

    const pickers: Record<AdvFilterStandardPicker, React.FC<IAdvFilterPickerProps>> = {
        [AdvFilterStandardPicker.Unit]: UnitPicker,
        [AdvFilterStandardPicker.Group]: GroupPicker,
        [AdvFilterStandardPicker.Channel]: ChannelPicker,
        [AdvFilterStandardPicker.Topic]: TopicPicker,
        [AdvFilterStandardPicker.TopicHierarchical]: TopicHierarchicalPicker,
        [AdvFilterStandardPicker.User]: UserPicker,
        [AdvFilterStandardPicker.CustomField]: CustomFieldPicker,
        [AdvFilterStandardPicker.Training]: TrainingPicker,
        [AdvFilterStandardPicker.Period]: PeriodPicker,
        [AdvFilterStandardPicker.Workplace]: WorkplacePicker,
        [AdvFilterStandardPicker.Sticker]: StickerPicker,
        [AdvFilterStandardPicker.Language]: LanguagePicker,
        [AdvFilterStandardPicker.Tag]: TagPicker,
        [AdvFilterStandardPicker.Switch]: SwitchPicker,
        [AdvFilterStandardPicker.TextField]: TextFieldPicker,
        [AdvFilterStandardPicker.DateField]: DateFieldPicker,
    };

    /// Adicionar o filtro na lista de seleção
    function addFilter(newFilter: IAdvFilterResult | IAdvFilterResult[], filterCommand: IAdvFilterCommand) {

        if (Array.isArray(newFilter)) {
            let newAdvFilters = [...filter.advFilters];
            for (const item of newFilter) {
                /** optionalGroup é utilizado para criar um Grupo de Opções Mutualmente Exclusiva (xor) dentro de um Grupo */
                const existCommand = filter.advFilters.find(x => x.command == item.command || (x.optionalGroup != null && item.optionalGroup != null && x.optionalGroup == item.optionalGroup));
                const existFilter = filter.advFilters.find(x => x.command == item.command && x.value == item.value);

                // Substitui o filtro
                if (existCommand && !filterCommand.canRepeat) {
                    newAdvFilters = [...newAdvFilters.filter(x => x != existCommand), item];
                }
                else if (existFilter) {
                    newAdvFilters = [...newAdvFilters.filter(x => x != existFilter), item];
                }
                // Adiciona o filtro
                else if (existFilter == undefined) {
                    newAdvFilters = [...newAdvFilters, item];
                }
            }
            onFilterChange({ ...filter, advFilters: newAdvFilters }, false);
        }
        else {
            /** optionalGroup é utilizado para criar um Grupo de Opções Mutualmente Exclusiva (xor) dentro de um Grupo */
            const existCommand = filter.advFilters.find(x => x.command == newFilter.command || (x.optionalGroup != null && newFilter.optionalGroup != null && x.optionalGroup == newFilter.optionalGroup));
            const existFilter = filter.advFilters.find(x => x.command == newFilter.command && x.value == newFilter.value);

            // Substitui o filtro
            if (existCommand && !filterCommand.canRepeat) {
                onFilterChange({ ...filter, advFilters: [...filter.advFilters.filter(x => x != existCommand), newFilter] }, false);
            }
            else if (existFilter) {
                onFilterChange({ ...filter, advFilters: [...filter.advFilters.filter(x => x != existFilter), newFilter] }, false);
            }
            // Adiciona o filtro
            else if (existFilter == undefined) {
                onFilterChange({ ...filter, advFilters: [...filter.advFilters, newFilter] }, false);
            }
        }

        setFilterPicker(null);
    }

    /// Cria um novo filtro a partir da seleção
    const createFilter = (filterCommand: IAdvFilterCommand) => {
        const newFilter: IAdvFilterResult = {
            command: filterCommand!.command,
            optionalGroup: filterCommand.optionGroup,
            text: `${filterCommand?.displayName.replace("...", "")}`,
            value: filterCommand.fixedValue,
            period: null,
            hideChip: filterCommand.hideChip,
            quickFilter: null,
            range: null,
        };

        addFilter(newFilter, filterCommand);
    };

    /// Ao selecionar uma opção de filtro do menu
    const handleSelectDropdownItem = (selectedCommand: IAdvFilterCommand) => {
        if (selectedCommand.picker == null && selectedCommand.CustomPicker == null) {
            if (selectedCommand.canCheck && isOptionActive(selectedCommand)) {
                const existFilter = filter.advFilters.find(x => x.command == selectedCommand.command && x.value == selectedCommand.fixedValue);
                onFilterChange({ ...filter, advFilters: filter.advFilters.filter(x => x != existFilter) }, false);
            }
            else {
                createFilter(selectedCommand);
            }
        }
        else {
            setFilterPicker(selectedCommand);
        }
    };

    // Ao remover um Filtro.
    const handleDelete = (item: IAdvFilterResult) => {
        if (item.command == quickFilter?.command) {
            quickFilter?.onRemoveQuickFilter(item);
        }
        else {
            const itemsRemoved = filter.advFilters.filter(x => x != item);
            onFilterChange({ ...filter, advFilters: itemsRemoved }, false);
        }
    };

    ///
    const isOptionActive = (option: IAdvFilterCommand): boolean => {
        return option.canCheck == true && filter.advFilters.some(x => x.command == option.command && x.value == option.fixedValue);
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECTS
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        if (filterPicker?.picker == null && filterPicker?.CustomPicker == null)
            searchRef.current?.setFocus();
    }, [filterPicker]);

    useDidUpdateEffect(() => {
        onFilterChange({ ...filter, order }, false);
    }, [order]);

    useDidUpdateEffect(() => {
        if (filter.order != order) setOrder(filter.order);
        if (filter.searchText) searchRef.current?.replaceSearchText(filter.searchText);
    }, [filter]);

    useEffect(() => {
        onEditing?.(filterPicker != null);
    }, [filterPicker]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    ///
    const CurrentPicker = filterPicker?.picker != null
        ? pickers[filterPicker.picker]
        : filterPicker?.CustomPicker ?? null;

    /// Renderiza os comandos em formato de CHIP.
    const renderChips = () => {
        const data = filter.advFilters.filter(p => p.hideChip != true);

        var groupResults: IAdvFilterGroupResult[] = [];

        for (const item of data.sort((a, b) => a.command.toString().localeCompare(b.command.toString()))) {
            var groupResult = groupResults.find(x => x.command == item.command);
            if (groupResult) {
                groupResult.results.push(item);
            } else {
                groupResults.push({
                    command: item.command,
                    title: "", //item.period ? "" : data.find(x => x.command == item.command)?.text ?? "",
                    results: [item]
                });
            }
        }

        return data.length == 0
            ? null
            : <Stack
                direction="row"
                justifyContent="flex-start"
                alignContent="flex-start"
                flexWrap="wrap"
                mt={2}
            >
                <>
                    {
                        data.map((item, index) => (
                            <MprcChip
                                key={`${item.command}${index}`}
                                sx={{ margin: "4px" }}
                                tabIndex={-1}
                                label={item.text}
                                title={item.text ?? undefined}
                                size="small"
                                onDelete={() => handleDelete(item)}
                            />
                        ))
                    }
                </>

            </Stack>;
    };

    ///
    const renderInputs = () => {
        return (
            <Grid container alignContent="center" alignItems="end" columnSpacing={2}>
                <Grid size="grow">
                    {
                        filterPicker != null && CurrentPicker != null
                            ? <CurrentPicker
                                filter={filterPicker}
                                onError={onError}
                                onApply={addFilter}
                                onCancel={() => setFilterPicker(null)}
                            />
                            : searchComponent
                                ? searchComponent
                                : <MprcSearchTextField
                                    ref={searchRef}
                                    initialValue={filter.searchText || ""}
                                    placeholder={placeholder ?? t("Search.TextPlaceholder")}
                                    confirmMode={searchTextConfirmMode}
                                    onConfirm={(value, enterPressed) => filter.searchText != value ? onFilterChange({ ...filter, searchText: value }, enterPressed) : undefined}
                                />
                    }
                </Grid>
            </Grid>
        );
    };

    ///
    const renderOwnActions = () => (
        <>
            {
                rightDropdowns &&
                rightDropdowns.map((option, index) =>
                    <AdvancedFilterDropdown
                        key={`advFilter-right-dropdown-${index}`}
                        textOrIcon={option.textOrIcon}
                        size="medium"
                        disabled={option.disabled}
                        title={option.title}
                        items={option.filterCommands.map(cmd => ({ text: cmd.displayName, value: cmd, group: cmd.group, checked: isOptionActive(cmd) == true }))}
                        color={option.filterCommands.filter(cmd => isOptionActive(cmd) == true).length > 0 ? "warning" : "inherit"}
                        onChange={(item) => handleSelectDropdownItem(item.value)}
                    />
                )
            }

            {afterRigthDropdown}

            {orderOptions.current &&
                <MprcIconButtonDropdown
                    key="orderBy"
                    menuItemCheck={true}
                    icon={<IconSort />}
                    title={t("shared:Filter.OrderBy")}
                    selected={filter.order}
                    items={orderOptions.current}
                    onChange={(order) => setOrder(order)}
                />
            }
        </>
    );

    ///
    const renderMenus = () => (
        <>
            {title &&
                <MprcFilterLine>
                    <Typography variant="h3">{title}</Typography>
                    <div>{renderOwnActions()}</div>
                </MprcFilterLine>
            }

            <MprcFilterLine>
                <Grid container alignItems="center" width="100%" spacing={1}>
                    {
                        startComponent && Array.isArray(startComponent)
                            ? startComponent.map((c, index) => <Grid key={index}>{c}</Grid>)
                            : startComponent && <Grid>{startComponent}</Grid>
                    }
                    <Grid size="grow">
                        <Grid container alignItems="center" alignContent="" width="100%" spacing={1}>
                            {
                                searchTextInFilterLine &&
                                <Grid size="grow">{renderInputs()}</Grid>
                            }

                            {
                                centerComponent &&
                                <Grid size="grow">{centerComponent}</Grid>
                            }

                            {
                                beforeLeftDropdown &&
                                <Grid size={searchTextInFilterLine || !!centerComponent ? undefined : "grow"}>
                                    <Grid container justifyContent="right">
                                        {
                                            beforeLeftDropdown.map((item, index) =>
                                                <Grid key={`advFilter-before-action-${index}`}>
                                                    {item}
                                                </Grid>
                                            )
                                        }
                                    </Grid>
                                </Grid>
                            }

                            {leftDropdowns.length > 0 &&
                                <Grid id="advfilter-left-dropdowns" size={centerComponent || beforeLeftDropdown || searchTextInFilterLine ? undefined : "grow"}>
                                    <Grid container columnSpacing={2} justifyContent={leftDropdownAlign}>
                                        {
                                            leftDropdowns.map((option, index) =>
                                                <Grid key={`advFilter-left-dropdown-${index}`}>
                                                    <AdvancedFilterDropdown
                                                        title={option.title}
                                                        size='medium'
                                                        textOrIcon={option.textOrIcon}
                                                        items={
                                                            option.filterCommands.map(cmd => ({ text: cmd.displayName, value: cmd, group: cmd.group, checked: isOptionActive(cmd) }))
                                                        }
                                                        onChange={(item) => handleSelectDropdownItem(item.value)}
                                                        onGetData={option.filterCommands.length > 0 || onGetStickers == null ? undefined : onGetStickers}
                                                    />
                                                </Grid>
                                            )
                                        }
                                    </Grid>
                                </Grid>
                            }

                            {!title &&
                                <Grid>
                                    {renderOwnActions()}
                                </Grid>
                            }
                        </Grid>
                    </Grid>
                    {
                        endComponent && Array.isArray(endComponent)
                            ? endComponent.map((c, index) => <Grid key={index}>{c}</Grid>)
                            : endComponent && <Grid>{endComponent}</Grid>
                    }
                </Grid>
            </MprcFilterLine>
        </>
    );

    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {chipsPosition == "top" && renderChips()}
            {renderMenus()}
            {message}
            {!searchTextInFilterLine && renderInputs()}
            {chipsPosition == "bottom" && renderChips()}
        </>
    );
};
